let serial = 0;
const finishes = [
  { name: 'iridescent-soap', centre: 0.1, edge: 0.8 },
  { name: 'opal-glass', centre: 0.18, edge: 0.88 },
  { name: 'cut-crystal', centre: 0.12, edge: 0.88 },
  { name: 'amber', centre: 0.22, edge: 0.92 },
  { name: 'frosted-glass', centre: 0.24, edge: 0.86 },
  { name: 'pearlescent-glass', centre: 0.16, edge: 0.88 },
];

function reflections(ctx, art, colour, kit) {
  const line = (d, stroke, width, opacity) =>
    kit.path(ctx, art, d, {
      fill: 'none',
      stroke,
      'stroke-width': width,
      opacity,
      'stroke-linecap': 'round',
    });
  switch (colour) {
    case 0:
      line('M16 54Q23 79 50 83', 'var(--game-colour-5)', 4, 0.55);
      line('M68 17Q84 26 87 45', 'var(--game-colour-4)', 3, 0.65);
      break;
    case 1:
      line('M19 52Q32 30 62 27Q74 27 81 36', 'var(--game-colour-2)', 6, 0.22);
      line('M24 70Q44 55 77 60', 'var(--game-white)', 7, 0.2);
      break;
    case 2:
      for (const [d, fill, opacity] of [
        ['M26 21L50 12L75 22L62 33L38 33Z', 'var(--game-white)', 0.3],
        ['M13 48L26 21L38 33L30 61L18 70Z', 'var(--game-white)', 0.15],
        ['M75 22L88 48L81 70L68 59L62 33Z', 'var(--game-colour-4)', 0.18],
        ['M18 70L30 61L68 59L81 70L50 88Z', 'var(--game-colour-2)', 0.2],
      ])
        kit.path(ctx, art, d, { fill, opacity });
      line('M26 21L38 33H62L75 22M18 70L30 61M68 59L81 70', 'var(--game-white)', 1.4, 0.6);
      break;
    case 3:
      line('M19 63Q41 76 79 66', 'var(--game-colour-1)', 6, 0.3);
      line('M25 75Q45 85 68 78', 'var(--game-colour-3)', 4, 0.8);
      kit.circle(ctx, art, 72, 43, 2.3, { fill: 'var(--game-colour-1)', opacity: 0.4 });
      kit.circle(ctx, art, 31, 63, 1.5, { fill: 'var(--game-white)', opacity: 0.65 });
      break;
    case 4:
      for (const d of ['M22 42L38 27', 'M23 63L70 20', 'M51 78L79 53'])
        line(d, 'var(--game-white)', 3, 0.23);
      line('M18 55Q23 75 42 81', 'var(--game-colour-0)', 5, 0.5);
      break;
    case 5:
      line('M18 42Q28 13 59 15', 'var(--game-colour-2)', 5, 0.4);
      line('M22 62Q46 80 76 61', 'var(--game-white)', 7, 0.24);
      line('M29 73Q53 87 76 68', 'var(--game-colour-5)', 3, 0.7);
      break;
  }
}

/** Six visible colour identities use round, translucent native materials.
 * Opacity lives in the shell's gradient stops: the scene remains visible through
 * the centre while a saturated rim and permanent outlined glyph preserve the
 * game's canonical colour/shape identity at dense phone sizes. No animation,
 * image request, hidden game state or model output is needed to render a piece. */
export function bubbleArtwork(ctx, cell, shape, kit) {
  const value = Number.isInteger(cell.colour) ? cell.colour : cell.value,
    colour = Number.isInteger(value) && value >= 0 && value < finishes.length ? value : 0,
    finish = finishes[colour],
    art = kit.svg(ctx, 'game-piece-art game-bubble-art'),
    id = `bubble-material-${++serial}`,
    glyph = cell.glyph || shape;
  art.setAttribute('data-material', finish.name);
  art.setAttribute('data-material-colour', String(colour));
  art.setAttribute('data-symbol', glyph);
  const defs = kit.add(ctx, art, 'defs', {});
  const shell = kit.add(ctx, defs, 'radialGradient', {
    id: `${id}-shell`,
    cx: '48%',
    cy: '44%',
    r: '56%',
  });
  for (const [offset, opacity] of [
    ['0%', finish.centre],
    ['48%', finish.centre],
    ['73%', 0.32],
    ['91%', finish.edge],
    ['100%', 0.72],
  ])
    kit.add(ctx, shell, 'stop', {
      offset,
      'stop-color': 'var(--piece-colour)',
      'stop-opacity': opacity,
    });
  const film = kit.add(ctx, defs, 'linearGradient', {
    id: `${id}-film`,
    x1: '0%',
    y1: '0%',
    x2: '90%',
    y2: '100%',
  });
  for (const [offset, stop] of [
    ['0%', 'var(--game-white)'],
    ['20%', 'var(--game-colour-0)'],
    ['40%', 'var(--game-colour-2)'],
    ['58%', 'var(--game-colour-5)'],
    ['78%', 'var(--game-colour-3)'],
    ['100%', 'var(--game-colour-4)'],
  ])
    kit.add(ctx, film, 'stop', { offset, 'stop-color': stop });
  kit.circle(ctx, art, 50, 50, 42, {
    'data-transparent-shell': 'true',
    fill: `url(#${id}-shell)`,
    stroke: 'var(--piece-colour)',
    'stroke-width': 2.5,
  });
  kit.circle(ctx, art, 50, 50, 39.7, {
    fill: 'none',
    stroke: 'var(--piece-colour)',
    'stroke-width': 3,
    opacity: 0.75,
  });
  kit.circle(ctx, art, 50, 50, 42, {
    fill: 'none',
    stroke: `url(#${id}-film)`,
    'stroke-width': 1.8,
    opacity: 0.85,
  });
  reflections(ctx, art, colour, kit);
  kit.path(ctx, art, 'M20 38Q25 21 43 18', {
    fill: 'none',
    stroke: 'var(--game-white)',
    'stroke-width': 5,
    'stroke-linecap': 'round',
    opacity: 0.95,
  });
  kit.path(ctx, art, 'M66 81Q81 74 85 57', {
    fill: 'none',
    stroke: 'var(--game-white)',
    'stroke-width': 2.5,
    'stroke-linecap': 'round',
    opacity: 0.7,
  });
  kit.circle(ctx, art, 64, 18, 2.2, { fill: 'var(--game-white)', opacity: 0.95 });
  const halo = kit.add(ctx, art, 'g', {
    transform: 'translate(50 50) scale(.85) translate(-50 -50)',
    'data-material-glyph-outline': 'true',
  });
  kit.symbol(ctx, halo, glyph, 'var(--game-white)');
  const mark = kit.add(ctx, art, 'g', {
    transform: 'translate(50 50) scale(.7) translate(-50 -50)',
    'data-material-glyph': 'true',
  });
  kit.symbol(ctx, mark, glyph, 'var(--game-paper-ink)');
  return art;
}
